import React, { useEffect, useState } from 'react';
import {
  Users, RefreshCw, Plus, CheckCircle, XCircle, Search,
  ChevronDown, X, Eye, EyeOff
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

interface PartnerUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  company_id: number;
  company_name: string;
  profile_id: number | null;
  profile_name: string | null;
}

interface Company {
  id: number;
  name: string;
}

const ASSIGNABLE_PROFILES = [
  { id: 2, name: 'Company Admin' },
  { id: 4, name: 'Compliance User' },
  { id: 6, name: 'Internal Auditor' },
];

const profileColor: Record<string, string> = {
  'Company Admin':    'bg-emerald-100 text-emerald-700',
  'Compliance User':  'bg-blue-100 text-blue-700',
  'Internal Auditor': 'bg-amber-100 text-amber-700',
};

function generatePassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function PartnerUsers() {
  const [users, setUsers]               = useState<PartnerUser[]>([]);
  const [companies, setCompanies]       = useState<Company[]>([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [showModal, setShowModal]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState<string | null>(null);
  const [createdCreds, setCreatedCreds] = useState<{ name: string; email: string; password: string } | null>(null);
  const [showPw, setShowPw]             = useState(false);
  const [toggling, setToggling]         = useState<number | null>(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '',
    company_id: '', profile_id: '2', password: generatePassword(),
  });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([
        fetch('/api/partner/users', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/partner/companies', { credentials: 'include' }).then(r => r.json()),
      ]);
      setUsers(Array.isArray(uRes.users) ? uRes.users : []);
      setCompanies(Array.isArray(cRes.companies) ? cRes.companies : []);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      const res = await fetch('/api/partner/users', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, company_id: form.company_id,
          profile_id: form.profile_id, password: form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');
      setCreatedCreds({ name: `${form.first_name} ${form.last_name}`, email: form.email, password: form.password });
      setShowModal(false);
      setForm({ first_name: '', last_name: '', email: '', company_id: '', profile_id: '2', password: generatePassword() });
      await load();
    } catch (err: any) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(user: PartnerUser) {
    setToggling(user.id);
    try {
      await fetch(`/api/partner/users/${user.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
    } finally {
      setToggling(null);
    }
  }

  const filtered = users.filter(u => {
    const matchSearch = !search || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchCompany = companyFilter === '' || u.company_id === companyFilter;
    return matchSearch && matchCompany;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-violet-100 rounded-xl">
            <Users className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Users</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Manage users across your portfolio companies</p>
          </div>
        </div>
        <button
          onClick={() => { setShowModal(true); setSaveError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Created credentials notice */}
      {createdCreds && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-emerald-800">User created — share these credentials</p>
            <p className="text-sm text-emerald-700 mt-1">
              <span className="font-medium">{createdCreds.name}</span> · {createdCreds.email}
            </p>
            <p className="text-sm text-emerald-700 font-mono mt-0.5">
              Temp password: <span className="font-semibold">{createdCreds.password}</span>
            </p>
          </div>
          <button onClick={() => setCreatedCreds(null)} className="text-emerald-500 hover:text-emerald-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text" placeholder="Search users…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div className="relative">
          <select
            value={companyFilter}
            onChange={e => setCompanyFilter(e.target.value === '' ? '' : parseInt(e.target.value))}
            className="appearance-none pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <span className="text-sm text-slate-500">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-12 text-slate-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No users found.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Last Login</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-left">
                    <div className="font-medium text-slate-900">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-left text-slate-600">{u.company_name}</td>
                  <td className="px-4 py-3 text-left">
                    {u.profile_name ? (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${profileColor[u.profile_name] || 'bg-slate-100 text-slate-600'}`}>
                        {u.profile_name}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 italic">No role</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-xs text-slate-400">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={toggling === u.id}
                      title={u.is_active ? 'Deactivate' : 'Reactivate'}
                      className={`p-1.5 rounded border transition-colors ${
                        u.is_active
                          ? 'border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500'
                          : 'border-slate-200 text-slate-400 hover:border-emerald-300 hover:text-emerald-600'
                      } ${toggling === u.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {u.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Add User</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">First Name *</label>
                  <input required type="text" value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Last Name *</label>
                  <input required type="text" value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Email *</label>
                <input required type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Company *</label>
                <select required value={form.company_id}
                  onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white">
                  <option value="">— Select company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Role *</label>
                <select value={form.profile_id}
                  onChange={e => setForm(f => ({ ...f, profile_id: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white">
                  {ASSIGNABLE_PROFILES.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Temporary Password *</label>
                <div className="relative">
                  <input required type={showPw ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono" />
                  <button type="button" onClick={() => setShowPw(p => !p)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Share this with the user — they should change it on first login.</p>
              </div>
              {saveError && <p className="text-sm text-red-600">{saveError}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors">
                  {saving ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
